import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Bold, 
  Italic, 
  Underline, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  List,
  ListOrdered,
  Save,
  Download,
  FileText,
  Printer,
  Eye,
  Type,
  Palette,
  Layout,
  Plus,
  Minus,
  RotateCcw,
  RotateCw
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { RichTextEditor } from "@/components/RichTextEditor";

const ResumeEditorNew = () => {
  const [content, setContent] = useState(`
    <h1>John Doe</h1>
    <p>Software Engineer</p>
    <p>john.doe@email.com | (555) 123-4567 | LinkedIn: linkedin.com/in/johndoe</p>
    
    <h2>Professional Summary</h2>
    <p>Experienced software engineer with 5+ years of expertise in full-stack development, specializing in React, Node.js, and cloud technologies. Proven track record of delivering scalable solutions and leading cross-functional teams.</p>
    
    <h2>Experience</h2>
    <h3>Senior Software Engineer - Tech Corp (2021 - Present)</h3>
    <ul>
      <li>Led development of customer-facing web applications serving 100K+ users</li>
      <li>Implemented CI/CD pipelines reducing deployment time by 50%</li>
      <li>Mentored 3 junior developers and conducted code reviews</li>
    </ul>
    
    <h3>Software Engineer - StartupXYZ (2019 - 2021)</h3>
    <ul>
      <li>Built responsive web applications using React and TypeScript</li>
      <li>Collaborated with design team to implement pixel-perfect UI components</li>
      <li>Optimized application performance resulting in 30% faster load times</li>
    </ul>
    
    <h2>Skills</h2>
    <p>JavaScript, TypeScript, React, Node.js, Python, AWS, Docker, Git, Agile/Scrum</p>
    
    <h2>Education</h2>
    <h3>Bachelor of Science in Computer Science</h3>
    <p>University of Technology (2015 - 2019)</p>
  `);
  
  const [selectedTemplate, setSelectedTemplate] = useState("modern");
  const [fontSize, setFontSize] = useState("12");
  const [fontFamily, setFontFamily] = useState("Arial");
  const { toast } = useToast();

  const handleSave = () => {
    toast({
      title: "Resume Saved",
      description: "Your resume has been saved successfully.",
    });
  };

  const handleDownload = () => {
    toast({
      title: "Download Started",
      description: "Your resume is being prepared for download.",
    });
  };

  const handleTemplateChange = (template: string) => {
    setSelectedTemplate(template);
    toast({
      title: "Template Changed",
      description: `Applied ${template} template to your resume.`,
    });
  };

  const formatCommands = [
    { icon: Bold, command: "bold", label: "Bold" },
    { icon: Italic, command: "italic", label: "Italic" },
    { icon: Underline, command: "underline", label: "Underline" },
  ];

  const alignmentCommands = [
    { icon: AlignLeft, command: "justifyLeft", label: "Align Left" },
    { icon: AlignCenter, command: "justifyCenter", label: "Center" },
    { icon: AlignRight, command: "justifyRight", label: "Align Right" },
    { icon: AlignJustify, command: "justifyFull", label: "Justify" },
  ];

  const templates = [
    { id: "modern", name: "Modern", color: "bg-blue-500" },
    { id: "classic", name: "Classic", color: "bg-gray-500" },
    { id: "creative", name: "Creative", color: "bg-purple-500" },
    { id: "minimal", name: "Minimal", color: "bg-green-500" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Toolbar */}
      <div className="border-b border-border bg-card">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-primary" />
              <h1 className="text-xl font-semibold">Resume Editor</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" size="sm">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button variant="default" size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <ResizablePanelGroup direction="horizontal" className="min-h-screen">
        {/* Document Editor Panel */}
        <ResizablePanel defaultSize={70} minSize={50}>
          <div className="h-full bg-muted/30 p-6 flex justify-center">
            <div className="w-full max-w-4xl">
              {/* Document Toolbar */}
              <div className="bg-card border border-border rounded-t-lg p-3 flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1 border-r border-border pr-3 mr-3">
                  <Select value={fontFamily} onValueChange={setFontFamily}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Arial">Arial</SelectItem>
                      <SelectItem value="Times New Roman">Times</SelectItem>
                      <SelectItem value="Helvetica">Helvetica</SelectItem>
                      <SelectItem value="Georgia">Georgia</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={fontSize} onValueChange={setFontSize}>
                    <SelectTrigger className="w-16">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="11">11</SelectItem>
                      <SelectItem value="12">12</SelectItem>
                      <SelectItem value="14">14</SelectItem>
                      <SelectItem value="16">16</SelectItem>
                      <SelectItem value="18">18</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-1 border-r border-border pr-3 mr-3">
                  {formatCommands.map((cmd) => (
                    <Button
                      key={cmd.command}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title={cmd.label}
                    >
                      <cmd.icon className="h-4 w-4" />
                    </Button>
                  ))}
                </div>

                <div className="flex items-center gap-1 border-r border-border pr-3 mr-3">
                  {alignmentCommands.map((cmd) => (
                    <Button
                      key={cmd.command}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title={cmd.label}
                    >
                      <cmd.icon className="h-4 w-4" />
                    </Button>
                  ))}
                </div>

                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Bullet List">
                    <List className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Numbered List">
                    <ListOrdered className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Document Editor */}
              <div className="bg-white border-l border-r border-b border-border rounded-b-lg shadow-lg min-h-[800px]">
                <RichTextEditor
                  value={content}
                  onChange={setContent}
                  placeholder="Start typing your resume..."
                  className="border-0 min-h-[800px]"
                  minHeight="800px"
                />
              </div>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Editing Controls Panel */}
        <ResizablePanel defaultSize={30} minSize={25} maxSize={45}>
          <ScrollArea className="h-full">
            <div className="p-6 space-y-6">
              {/* Templates Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layout className="h-5 w-5" />
                    Templates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className={`cursor-pointer border rounded-lg p-3 text-center transition-all hover:shadow-md ${
                          selectedTemplate === template.id
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => handleTemplateChange(template.id)}
                      >
                        <div className={`w-full h-12 ${template.color} rounded mb-2`} />
                        <p className="text-sm font-medium">{template.name}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Formatting Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Type className="h-5 w-5" />
                    Format
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Font Family</Label>
                    <Select value={fontFamily} onValueChange={setFontFamily}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Arial">Arial</SelectItem>
                        <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                        <SelectItem value="Helvetica">Helvetica</SelectItem>
                        <SelectItem value="Georgia">Georgia</SelectItem>
                        <SelectItem value="Calibri">Calibri</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Font Size</Label>
                    <Select value={fontSize} onValueChange={setFontSize}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10pt</SelectItem>
                        <SelectItem value="11">11pt</SelectItem>
                        <SelectItem value="12">12pt</SelectItem>
                        <SelectItem value="14">14pt</SelectItem>
                        <SelectItem value="16">16pt</SelectItem>
                        <SelectItem value="18">18pt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-3 gap-2">
                    {formatCommands.map((cmd) => (
                      <Button
                        key={cmd.command}
                        variant="outline"
                        size="sm"
                        className="h-8"
                        title={cmd.label}
                      >
                        <cmd.icon className="h-4 w-4" />
                      </Button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {alignmentCommands.map((cmd) => (
                      <Button
                        key={cmd.command}
                        variant="outline"
                        size="sm"
                        className="h-8"
                        title={cmd.label}
                      >
                        <cmd.icon className="h-4 w-4" />
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Colors Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    Colors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-6 gap-2">
                    {[
                      "#000000", "#333333", "#666666", "#999999", "#CCCCCC", "#FFFFFF",
                      "#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF",
                      "#800000", "#008000", "#000080", "#808000", "#800080", "#008080"
                    ].map((color) => (
                      <div
                        key={color}
                        className="w-8 h-8 rounded cursor-pointer border border-border hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button variant="outline" className="w-full justify-start">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Section
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Undo
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <RotateCw className="h-4 w-4 mr-2" />
                    Redo
                  </Button>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};

export default ResumeEditorNew;